<template>
    <div class="thread-users-container">
        <h2>{{ threadTitle }}</h2>

        <h3>Members</h3>

        <ThreadUsersTableList
            ref="threadUsersTableList"
            :records="threadUsers"
            :label-key="'name'"
            :is-app-support="currentThread?.is_support_thread"
            @view="handleViewUser"
            @message="handleMessageUser"
            @delete="handleRemoveUser"
        />

        <div v-if="!exitedThread" class="d-flex justify-content-end">
            <button class="btn btn-primary" @click="showAddUser = true">
                Add Member
                <i class="fi fi-add"></i>
            </button>
        </div>

        <form
            v-if="showAddUser"
            @submit.prevent="handleAddUser"
            class="d-flex flex-column gap-2"
        >
            <Field
                label="Select Member"
                type="multi-select"
                :key="unSelectedUsers?.length ?? -1"
                :params="{
                    options: unSelectedUsers,
                }"
                v-model="selectedUsers"
            />
            <div class="d-flex justify-content-end" style="gap: 0.5rem">
                <button
                    type="button"
                    class="btn btn-white"
                    @click="showAddUser = false"
                >
                    Cancel
                </button>
                <button type="submit" class="btn btn-secondary">Save</button>
            </div>
        </form>
    </div>
</template>

<script>
import Field from '@/components/Field.vue';
import ThreadUsersTableList from '@/components/messaging/ThreadUsersTableList.vue';
import { useMessengerStore } from '@/stores/messenger';

export default {
    components: {
        Field,
        ThreadUsersTableList,
    },
    props: {
        threadId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            showAddUser: false,
            allUsers: [],
            selectedUsers: [],
            currentThread: null,
            messengerStore: null,
        };
    },
    computed: {
        exitedThread() {
            const threadUser = this.currentThread?.users.find((threadUser) => {
                return threadUser.user.id == this.$userStore.user.id;
            });
            return threadUser?.exited_at;
        },
        threadUsers() {
            if (this.currentThread) {
                let filterUsers = this.currentThread.users;

                if (this.currentThread.is_support_thread) {
                    filterUsers = filterUsers.filter(
                        (user) => !user.user.is_app_support
                    );
                }

                const mutatedUsers = filterUsers.map((user) => {
                    const hash =
                        this.$employeesStore.getEmployeeById(user.user.id)
                            ?.hash || null;
                    return {
                        ...user.user,
                        name: user.user.first_name + ' ' + user.user.last_name,
                        hash: hash,
                        isExited: !!user.exited_at,
                    };
                });

                mutatedUsers.sort((a, b) => {
                    if (a.isExited === b.isExited) return 0;
                    return a.isExited ? 1 : -1;
                });

                return mutatedUsers;
            }
            return [];
        },
        threadTitle() {
            return this.currentThread?.subject || 'Thread';
        },
        unSelectedUsers() {
            return this.allUsers.filter((user) => {
                return !this.threadUsers.some(
                    (threadUser) => threadUser.id == user.value
                );
            });
        },
    },
    methods: {
        fetchCurrentThreadUsers() {
            const payload = {
                where: {
                    id: this.threadId,
                },
                include: {
                    users: {
                        include: {
                            user: true,
                        },
                    },
                },
            };
            this.$getData('message-thread/read', payload).then((response) => {
                if (response?.success && response?.data?.length > 0) {
                    this.currentThread = response.data[0];
                }
            });
        },
        handleViewUser(user) {
            if (user?.hash) {
                this.$router.push({
                    name: 'Employee Detail',
                    params: {
                        hash: user?.hash,
                    },
                });
                this.messengerStore.closePanel();
            }
        },
        handleMessageUser(user) {
            if (user.id) {
                this.messengerStore.openNew([user.id]);
            }
        },
        handleRemoveUser(user) {
            if (user.id) {
                this.$postData('message-thread/user/remove', {
                    thread_id: this.threadId,
                    user_id: user.id,
                }).then((response) => {
                    if (response?.success) {
                        if (this.$refs.threadUsersTableList) {
                            this.$refs.threadUsersTableList?.closeModal();
                        }
                        if (this.$userStore.user.id == user.id) {
                            this.$alertStore.pushSuccess(
                                'You have successfully left the thread'
                            );
                            this.messengerStore.openHistory();
                            return;
                        }
                        this.$alertStore.pushSuccess(
                            'User has been removed from the thread'
                        );
                        this.fetchCurrentThreadUsers();
                    } else {
                        this.$alertStore.pushError(
                            response?.message ||
                                'Failed to remove user from the thread'
                        );
                    }
                });
            }
        },
        handleAddUser() {
            if (this.selectedUsers.length > 0) {
                this.$postData('message-thread/user/add', {
                    thread_id: this.threadId,
                    user_ids: this.selectedUsers.map((userId) =>
                        parseInt(userId)
                    ),
                }).then((response) => {
                    if (response?.success) {
                        this.showAddUser = false;
                        this.selectedUsers = [];
                        this.$alertStore.pushSuccess(
                            'Users have been added to the thread'
                        );
                        this.fetchCurrentThreadUsers();
                    } else {
                        this.$alertStore.pushError(
                            response?.message ||
                                'Failed to add user to the thread'
                        );
                    }
                });
            }
        },
    },
    mounted() {
        this.messengerStore = useMessengerStore();
        this.fetchCurrentThreadUsers();
        this.$userStore.sync(() => {
            this.$employeesStore.sync(() => {
                // load with all users
                this.allUsers = this.$employeesStore.getEmployeeOptions();
            });
        });
    },
};
</script>

<style scoped>
.thread-users-container {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
}
</style>
